import useUserPermissionsStore from "@/stores/useUserPermissionsStore";
import { cn } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useCalendarStore } from "../../store/useCalendarStore";
import { slotAtYOffset } from "../hooks/slotAtYOffset";

/** Only the parent column and `data-slot-surface` elements count as empty grid. */
export function SlotHoverBand() {
  const ref = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const { slotHeight, startTime, timeStep, calendarHeight } = useCalendarStore(
    (state) => state.calendarData,
  );
  const canBook = Boolean(
    useUserPermissionsStore((state) => state.permissions.manage_appointments),
  );

  useEffect(() => {
    const column = ref.current?.parentElement;
    if (!column || !canBook) return;

    function onMouseMove(event: MouseEvent) {
      if (!column) return;
      const { target } = event;
      const isOverEmptyGrid =
        target === column ||
        (target instanceof Element && target.hasAttribute("data-slot-surface"));
      // A held button means a drag is under way, which shows its own time.
      if (!isOverEmptyGrid || event.buttons !== 0) {
        setHoveredIndex(null);
        return;
      }

      const yOffset = event.clientY - column.getBoundingClientRect().top;
      setHoveredIndex(
        slotAtYOffset({ yOffset, slotHeight, startTime, timeStep }).index,
      );
    }

    function onMouseLeave() {
      setHoveredIndex(null);
    }

    column.addEventListener("mousemove", onMouseMove);
    column.addEventListener("mouseleave", onMouseLeave);
    return () => {
      column.removeEventListener("mousemove", onMouseMove);
      column.removeEventListener("mouseleave", onMouseLeave);
      setHoveredIndex(null);
    };
  }, [canBook, slotHeight, startTime, timeStep]);

  const top = (hoveredIndex ?? 0) * slotHeight;
  const isVisible = canBook && hoveredIndex != null && top < calendarHeight;
  const { time } = slotAtYOffset({
    yOffset: top,
    slotHeight,
    startTime,
    timeStep,
  });

  return (
    <div
      ref={ref}
      aria-hidden
      data-identifier="calendar-slot-hover"
      className={cn(
        "pointer-events-none absolute inset-x-0 rounded-[6px] bg-gray-500/10 dark:bg-white/10",
        !isVisible && "hidden",
      )}
      style={{ top, height: slotHeight }}
    >
      <span className="absolute top-1/2 left-1.5 -translate-y-1/2 rounded-[5px] bg-white px-1.5 font-bold text-[12px] text-gray-700 leading-[18px] shadow-sm dark:bg-neutral-800 dark:text-gray-200">
        {time}
      </span>
    </div>
  );
}
